import type { StoredAuthSession } from './stored-auth-session';

const MIN_IDLE_TIMEOUT_MS = 60 * 1000;

export interface SessionIdleState {
  idleTimeoutMs: number;
  lastActivityAt: string;
  tokenReceivedAt: string;
}

export function createSessionIdleState(
  expiry: string,
  receivedAtMs = Date.now(),
  lastActivityAt?: string): SessionIdleState {
  const receivedAt = new Date(receivedAtMs).toISOString();

  return {
    // WHY: Core currently returns an absolute token expiry, not the resolved SessionTimeout
    // permission. The difference at receipt is therefore the cleanest client-only source for
    // the sliding idle duration; network latency can only make it slightly more conservative.
    idleTimeoutMs: deriveIdleTimeoutMs(expiry, receivedAtMs),
    lastActivityAt: lastActivityAt ?? receivedAt,
    tokenReceivedAt: receivedAt
  };
}

export function ensureSessionIdleState(
  session: StoredAuthSession,
  nowMs = Date.now()): StoredAuthSession & SessionIdleState {
  if (hasValidIdleState(session)) {
    return session;
  }

  // WHY: Sessions created before sliding-idle metadata existed must not be logged out
  // immediately on deployment. Give the existing session one remaining-token grace window;
  // the next login or refresh replaces this estimate with a full token-derived duration.
  return {
    ...session,
    ...createSessionIdleState(session.expiry, nowMs)
  };
}

export function getSessionIdleDeadlineMs(session: StoredAuthSession): number | null {
  if (!hasValidIdleState(session)) {
    return null;
  }

  const lastActivityAtMs = Date.parse(session.lastActivityAt);
  return Number.isFinite(lastActivityAtMs)
    ? lastActivityAtMs + session.idleTimeoutMs
    : null;
}

export function isSessionIdleExpired(session: StoredAuthSession, nowMs = Date.now()): boolean {
  const deadlineMs = getSessionIdleDeadlineMs(session);
  return deadlineMs !== null && nowMs >= deadlineMs;
}

export function hasActivitySinceTokenReceived(session: StoredAuthSession): boolean {
  if (!hasValidIdleState(session)) {
    return false;
  }

  return Date.parse(session.lastActivityAt) > Date.parse(session.tokenReceivedAt);
}

export function withSessionActivity(
  session: StoredAuthSession,
  activityAtMs = Date.now()): StoredAuthSession & SessionIdleState {
  const sessionWithIdleState = ensureSessionIdleState(session, activityAtMs);
  const currentActivityAtMs = Date.parse(sessionWithIdleState.lastActivityAt);

  return {
    ...sessionWithIdleState,
    lastActivityAt: new Date(Math.max(currentActivityAtMs, activityAtMs)).toISOString()
  };
}

export function withRefreshedSessionIdleState(
  refreshedSession: StoredAuthSession,
  previousSession: StoredAuthSession | null,
  receivedAtMs = Date.now()): StoredAuthSession & SessionIdleState {
  const previousActivity = previousSession
    ? ensureSessionIdleState(previousSession, receivedAtMs).lastActivityAt
    : undefined;

  // WHY: Refresh extends bearer validity but is not user activity. Preserving this timestamp
  // ensures activity at minute 10 still expires at minute 70 after a minute-55 JWT refresh.
  return {
    ...refreshedSession,
    ...createSessionIdleState(refreshedSession.expiry, receivedAtMs, previousActivity)
  };
}

function deriveIdleTimeoutMs(expiry: string, receivedAtMs: number): number {
  const expiryMs = Date.parse(expiry);
  if (!Number.isFinite(expiryMs)) {
    return MIN_IDLE_TIMEOUT_MS;
  }

  return Math.max(MIN_IDLE_TIMEOUT_MS, expiryMs - receivedAtMs);
}

function hasValidIdleState(session: StoredAuthSession): session is StoredAuthSession & SessionIdleState {
  return typeof session.idleTimeoutMs === 'number'
    && Number.isFinite(session.idleTimeoutMs)
    && session.idleTimeoutMs > 0
    && typeof session.lastActivityAt === 'string'
    && Number.isFinite(Date.parse(session.lastActivityAt))
    && typeof session.tokenReceivedAt === 'string'
    && Number.isFinite(Date.parse(session.tokenReceivedAt));
}
